import React, { useState, useEffect } from 'react';
import { Star } from 'lucide-react';
import { reviewsAPI } from '../utils/api';

// Public testimonials section for the landing page.
// Renders nothing until there is at least one APPROVED review, so the
// AggregateRating schema always matches visible, real reviews (Google-compliant).
const Testimonials = () => {
  const [data, setData] = useState({ reviews: [], average: 0, count: 0 });

  useEffect(() => {
    reviewsAPI.getPublic()
      .then((res) => setData(res.data || { reviews: [], average: 0, count: 0 }))
      .catch(() => {});
  }, []);

  const { reviews, average, count } = data;
  if (!count || reviews.length === 0) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'MoneyFeed',
    description: 'WhatsApp accounting & bookkeeping for freelancers and small businesses.',
    brand: { '@type': 'Brand', name: 'MoneyFeed' },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: String(average),
      reviewCount: String(count),
      bestRating: '5',
      worstRating: '1',
    },
    review: reviews.slice(0, 12).map((r) => ({
      '@type': 'Review',
      reviewRating: { '@type': 'Rating', ratingValue: String(r.rating), bestRating: '5', worstRating: '1' },
      author: { '@type': 'Person', name: r.display_name || 'MoneyFeed user' },
      reviewBody: r.comment || '',
    })),
  };

  return (
    <section className="mb-16" data-testid="testimonials-section">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 mb-3">
          <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} className={`h-5 w-5 ${i <= Math.round(average) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} />
            ))}
          </div>
          <span className="font-bold text-[#064E3B]">{average}</span>
          <span className="text-gray-500 text-sm">from {count} review{count > 1 ? 's' : ''}</span>
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-[#064E3B] mb-3">Loved by freelancers</h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">Real reviews from people who do their books on WhatsApp with MoneyFeed.</p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {reviews.map((r) => (
          <div key={r.id} className="bg-white rounded-2xl shadow-md p-6 border border-gray-100" data-testid={`testimonial-${r.id}`}>
            <div className="flex gap-0.5 mb-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} className={`h-4 w-4 ${i <= r.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} />
              ))}
            </div>
            {r.comment && <p className="text-gray-700 text-sm mb-4 leading-relaxed">“{r.comment}”</p>}
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#064E3B] text-white font-bold flex items-center justify-center text-sm">
                {(r.display_name || 'M').charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="font-semibold text-[#064E3B] text-sm leading-tight">{r.display_name}</p>
                {(r.role || r.country) && (
                  <p className="text-gray-400 text-xs">{[r.role, r.country].filter(Boolean).join(' · ')}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Testimonials;
